import { useNavigate } from 'react-router-dom'
import { Pencil, Trash2, FolderOpen, Calendar } from 'lucide-react'
import { CardSkeleton } from '../LoadingSkeleton'
import { formatDate } from '../../utils/dashboardConstants'

export default function ProjectsGrid({ projects, loadingProjects, handleEdit, handleDelete }) {
  const navigate = useNavigate()

  return (
    <div className="bg-white shadow-xl rounded-2xl p-6 sm:p-8 mb-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FolderOpen className="w-6 sm:w-7 h-6 sm:h-7 text-blue-600" />
          My Projects
        </h2>
        <button
          onClick={() => navigate('/add-project')}
          className="group relative w-full sm:w-auto bg-gradient-to-r from-blue-600 via-blue-600 to-blue-600 hover:from-blue-700 hover:via-blue-700 hover:to-blue-700 text-white px-5 sm:px-7 py-2.5 sm:py-3 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base font-semibold overflow-hidden transform hover:scale-105"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          <svg className="w-5 h-5 relative z-10 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          <span className="relative z-10">Add Project</span>
        </button>
      </div>

      {loadingProjects ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-12">
          <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Belum ada project. Yuk tambahkan satu!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div
              key={project.project_id}
              className="group relative backdrop-blur-xl bg-white/80 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-5 border border-white/40 overflow-hidden transform hover:-translate-y-2"
            >
              {/* Shimmer Effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none project-card-shimmer"></div>

              {/* Gradient Glow on Hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-blue-500/0 to-pink-500/0 group-hover:from-blue-500/5 group-hover:via-blue-500/5 group-hover:to-pink-500/5 rounded-2xl transition-all duration-500"></div>

              <div className="relative z-10">
                <img
                  src={project.image_url || 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=300&fit=crop'}
                  alt={project.title}
                  className="w-full h-40 object-cover rounded-lg mb-4"
                  onError={(e) => {
                    e.target.onerror = null
                    e.target.src = 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=300&fit=crop'
                  }}
                />
                <h3 className="text-lg font-bold text-gray-800 mb-2">{project.title}</h3>

                {project.role && (
                  <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full mb-2">
                    {project.role}
                  </span>
                )}

                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{project.description}</p>

                {project.start_date && project.end_date && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>
                      {formatDate(project.start_date)} - {formatDate(project.end_date)}
                    </span>
                  </div>
                )}

                <p className="text-xs text-gray-400 mb-4">
                  📅 Dibuat: {formatDate(project.created_at)}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(project.project_id)}
                    className="group relative flex-1 flex items-center justify-center gap-1.5 text-sm text-white bg-gradient-to-br from-blue-600 via-blue-600 to-blue-600 hover:from-blue-700 hover:via-blue-700 hover:to-blue-700 px-3 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 font-semibold overflow-hidden transform hover:scale-105"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
                    <Pencil className="w-4 h-4 relative z-10 group-hover:rotate-12 transition-transform duration-300" />
                    <span className="relative z-10">Edit</span>
                  </button>
                  <button
                    onClick={() => handleDelete(project.project_id)}
                    className="group relative flex-1 flex items-center justify-center gap-1.5 text-sm text-white bg-gradient-to-br from-rose-600 via-red-600 to-pink-600 hover:from-rose-700 hover:via-red-700 hover:to-pink-700 px-3 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 font-semibold overflow-hidden transform hover:scale-105"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
                    <Trash2 className="w-4 h-4 relative z-10 group-hover:scale-110 transition-transform duration-300" />
                    <span className="relative z-10">Hapus</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
